/**
 * Kitchen cabinet listing spec (Wave 2 kitchen cabinet mount Phase 1).
 *
 * Canonical id: `kitchen-cabinet` (new). Legacy `hanging-kitchen-cabinet` is
 * an alias for reads/backfill. Required: `kitchen_cabinet_mount_type`.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const KITCHEN_CABINET_MOUNT_TYPES = ["floor", "hanging"] as const;
export type KitchenCabinetMountType =
  (typeof KITCHEN_CABINET_MOUNT_TYPES)[number];

export const CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID =
  "kitchen-cabinet" as const;

export const LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS = [
  "hanging-kitchen-cabinet",
] as const;

export type LegacyKitchenCabinetSubcategoryId =
  (typeof LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS)[number];

export const KITCHEN_CABINET_SUBCATEGORY_IDS = [
  CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
  ...LEGACY_KITCHEN_CABINET_SUBCATEGORY_IDS,
] as const;

export type KitchenCabinetSubcategoryId =
  (typeof KITCHEN_CABINET_SUBCATEGORY_IDS)[number];

export type LegacyKitchenCabinetSubcategoryMapping = {
  legacyId: string;
  canonicalId: typeof CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID;
  kitchenCabinetMountType: KitchenCabinetMountType;
};

/** Locked Phase 0 alias map (SIO + reference backfill). */
export const LEGACY_KITCHEN_CABINET_SUBCATEGORY_MAP: readonly LegacyKitchenCabinetSubcategoryMapping[] =
  [
    {
      legacyId: "hanging-kitchen-cabinet",
      canonicalId: CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID,
      kitchenCabinetMountType: "hanging",
    },
  ] as const;

const mapByLegacyId = new Map(
  LEGACY_KITCHEN_CABINET_SUBCATEGORY_MAP.map((row) => [row.legacyId, row]),
);

export type KitchenCabinetSpecFormState = {
  kitchenCabinetMountType: KitchenCabinetMountType | "";
};

export function defaultKitchenCabinetSpecFormState(): KitchenCabinetSpecFormState {
  return {
    kitchenCabinetMountType: "",
  };
}

export function isKitchenCabinetMountType(
  value: unknown,
): value is KitchenCabinetMountType {
  return (
    typeof value === "string" &&
    (KITCHEN_CABINET_MOUNT_TYPES as readonly string[]).includes(value)
  );
}

export function isKitchenCabinetSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is KitchenCabinetSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (KITCHEN_CABINET_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  );
}

export function isCanonicalKitchenCabinetSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID;
}

export function getKitchenCabinetSpecSectionLabel(): string {
  return "Kitchen cabinet details";
}

export function resolveCanonicalKitchenCabinetSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID) {
    return CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID;
  }
  return mapByLegacyId.get(subcategoryId)?.canonicalId ?? null;
}

export function getLegacyKitchenCabinetSubcategoryMapping(
  subcategoryId: string | null | undefined,
): LegacyKitchenCabinetSubcategoryMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

export function inferKitchenCabinetAttrsFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): { kitchenCabinetMountType: KitchenCabinetMountType } | null {
  const mapped = getLegacyKitchenCabinetSubcategoryMapping(subcategoryId);
  if (!mapped) return null;
  return { kitchenCabinetMountType: mapped.kitchenCabinetMountType };
}

/**
 * Clear non-cabinet titles currently parked on hanging-kitchen-cabinet.
 * Do not set kitchen-cabinet attrs; reference backfill should mark review.
 */
export function isExcludedFromKitchenCabinetBackfillTitle(
  title: string | null | undefined,
): boolean {
  const t = String(title ?? "").toLowerCase();
  if (!t.trim()) return false;
  if (/kitchen\s*sink/.test(t)) return true;
  if (/extractor/.test(t) && !/cabinet/.test(t)) return true;
  if (/marble\s*kitchen\s*top/.test(t) && !/cabinet/.test(t)) return true;
  return false;
}

const HANGING_TITLE_RE =
  /hang(ing|ed)|wall[\s-]?mount(ed)?|overhead|wall[\s-]?hung/;
const FLOOR_TITLE_RE =
  /\bfloor\b|\bstanding\b|base\s*cabinet|marble\s*top|with\s*basin|cupboard|kitchen\s*unit|\d+\s*door/;

export function inferKitchenCabinetMountTypeFromTitle(
  title: string | null | undefined,
): KitchenCabinetMountType | null {
  const t = String(title ?? "").toLowerCase();
  const hangingMatch = t.match(HANGING_TITLE_RE);
  const floorMatch = t.match(FLOOR_TITLE_RE);
  if (!hangingMatch && !floorMatch) return null;

  const hangingIdx = hangingMatch?.index ?? -1;
  const floorIdx = floorMatch?.index ?? -1;
  if (hangingIdx < 0) return "floor";
  if (floorIdx < 0) return "hanging";
  return floorIdx > hangingIdx ? "floor" : "hanging";
}

/**
 * Resolve mount from legacy id defaults, then title overrides.
 * Returns null when title is excluded or id is not a cabinet alias/canonical.
 */
export function resolveKitchenCabinetAttrsForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): { kitchenCabinetMountType: KitchenCabinetMountType } | null {
  if (isExcludedFromKitchenCabinetBackfillTitle(title)) return null;

  let defaults = inferKitchenCabinetAttrsFromLegacySubcategoryId(subcategoryId);
  if (!defaults && subcategoryId === CANONICAL_KITCHEN_CABINET_SUBCATEGORY_ID) {
    // D6: ambiguous / missing title → hanging
    defaults = { kitchenCabinetMountType: "hanging" };
  }
  if (!defaults) return null;

  const mountFromTitle = inferKitchenCabinetMountTypeFromTitle(title);
  return {
    kitchenCabinetMountType: mountFromTitle ?? defaults.kitchenCabinetMountType,
  };
}

export function formatKitchenCabinetMountTitleWord(
  mount: KitchenCabinetMountType,
): string {
  return mount === "floor" ? "Floor" : "Hanging";
}

/**
 * Locked: mount → brand? → Kitchen Cabinet
 */
export function composeKitchenCabinetTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const mount = attrs.kitchen_cabinet_mount_type;
  if (!isKitchenCabinetMountType(mount)) return "";

  return composeListingTitleFromSlotsWithIdentity(
    {
      form: formatKitchenCabinetMountTitleWord(mount),
      product: "Kitchen Cabinet",
    },
    identity,
  );
}

export function buildKitchenCabinetAttributesFromForm(
  form: KitchenCabinetSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isKitchenCabinetMountType(form.kitchenCabinetMountType)) {
    out.kitchen_cabinet_mount_type = form.kitchenCabinetMountType;
  }
  return out;
}

export function getKitchenCabinetSpecFormValidationMessage(
  form: KitchenCabinetSpecFormState,
): string | undefined {
  const issues = validateStoredKitchenCabinetAttributes(
    buildKitchenCabinetAttributesFromForm(form),
  );
  return issues[0]?.message;
}

export type KitchenCabinetFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

/** Hydrate sell/edit form from stored `items.attributes` (+ legacy alias fallback). */
export function kitchenCabinetFormFromAttributes(
  attributes: unknown,
  options?: KitchenCabinetFormHydrateOptions,
): KitchenCabinetSpecFormState {
  const base = defaultKitchenCabinetSpecFormState();
  const attrs = asListingSpecAttributes(attributes);
  let kitchenCabinetMountType: KitchenCabinetMountType | "" =
    isKitchenCabinetMountType(attrs.kitchen_cabinet_mount_type)
      ? attrs.kitchen_cabinet_mount_type
      : base.kitchenCabinetMountType;

  const legacyMap = getLegacyKitchenCabinetSubcategoryMapping(
    options?.subcategoryId,
  );
  if (
    legacyMap &&
    !kitchenCabinetMountType &&
    !isExcludedFromKitchenCabinetBackfillTitle(options?.title)
  ) {
    const resolved = resolveKitchenCabinetAttrsForLegacySubcategory(
      options?.subcategoryId,
      options?.title,
    );
    if (resolved) {
      kitchenCabinetMountType = resolved.kitchenCabinetMountType;
    }
  }

  return { kitchenCabinetMountType };
}

export function validateStoredKitchenCabinetAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;
  const mount = attrs.kitchen_cabinet_mount_type;

  if (mount === undefined || mount === null || mount === "") {
    issues.push({
      key: "kitchen_cabinet_mount_type",
      code: "required",
      message: "Choose floor or hanging.",
    });
  } else if (!isKitchenCabinetMountType(mount)) {
    issues.push({
      key: "kitchen_cabinet_mount_type",
      code: "invalid_enum",
      message: "Mount type must be floor or hanging.",
    });
  }

  return issues;
}
